// Pre-translated clause library for rental-agreement PDFs.
//
// KEY INSIGHT: the English sentence IS the library key. No separate IDs.
// This means:
//   - Every existing agreement that uses the default English clause
//     gets auto-translated into whichever language the PDF is rendered.
//   - Any custom English text the landlord types that doesn't match a
//     library entry falls through to the legacy "print verbatim" path —
//     same as before this library existed.
//
// To extend: add another key/value pair under the relevant clause field
// where the key is the English sentence exactly, and the value is an
// object with the 8 non-English translations (en is implicit — it's the
// key itself).
//
// Why this matters: it solves the "custom clauses don't translate"
// problem that the user flagged. Landlords who stick with the bundled
// defaults get a fully-translated PDF in every language for free; only
// genuinely-custom clauses stay in the author's language.

export const CLAUSE_LIBRARY = {
    electricity_clause: {
        "Tenant shall bear and pay all electricity charges, water charges, and garbage tax at his own cost.": {
            ta: "மின்சாரம், தண்ணீர் மற்றும் குப்பை வரி ஆகியவற்றை குத்தகைதாரர் தன் சொந்த செலவில் செலுத்த வேண்டும்.",
            hi: "किरायेदार सभी बिजली, पानी और कचरा कर अपने खर्च पर वहन करेगा।",
            mr: "भाडेकरू वीज, पाणी आणि कचरा कर स्वतःच्या खर्चाने भरेल.",
            kn: "ಬಾಡಿಗೆದಾರ ವಿದ್ಯುತ್, ನೀರು ಮತ್ತು ಕಸದ ತೆರಿಗೆಯನ್ನು ತನ್ನ ಸ್ವಂತ ಖರ್ಚಿನಲ್ಲಿ ಭರಿಸಬೇಕು.",
            te: "అద్దెదారు విద్యుత్, నీరు మరియు చెత్త పన్నులను తన సొంత ఖర్చుతో చెల్లించాలి.",
            ml: "വാടകക്കാരൻ വൈദ്യുതി, വെള്ളം, മാലിന്യ നികുതി എന്നിവ സ്വന്തം ചെലവിൽ വഹിക്കണം.",
            bn: "ভাড়াটিয়া বিদ্যুৎ, জল ও আবর্জনা করের সমস্ত খরচ নিজস্ব খরচে বহন করবেন।",
            gu: "ભાડુઆત વીજળી, પાણી અને કચરા કરના તમામ ખર્ચ પોતાના ખર્ચે વહન કરશે.",
        },
        "Electricity and water charges to be borne by the tenant.": {
            ta: "மின்சாரம் மற்றும் தண்ணீர் கட்டணங்களை குத்தகைதாரர் செலுத்த வேண்டும்.",
            hi: "बिजली और पानी के शुल्क किरायेदार द्वारा वहन किए जाएंगे।",
            mr: "वीज आणि पाणी बिले भाडेकरूने भरावी लागतील.",
            kn: "ವಿದ್ಯುತ್ ಮತ್ತು ನೀರಿನ ಶುಲ್ಕಗಳನ್ನು ಬಾಡಿಗೆದಾರ ಭರಿಸಬೇಕು.",
            te: "విద్యుత్ మరియు నీటి ఛార్జీలను అద్దెదారు భరించాలి.",
            ml: "വൈദ്യുതി, വെള്ളം ചാർജുകൾ വാടകക്കാരൻ വഹിക്കേണ്ടതാണ്.",
            bn: "বিদ্যুৎ ও জলের খরচ ভাড়াটিয়া বহন করবেন।",
            gu: "વીજળી અને પાણીના ચાર્જ ભાડુઆતે ચૂકવવાના રહેશે.",
        },
    },

    property_tax_clause: {
        "Landlord shall pay the property tax for the premises.": {
            ta: "வளாகத்திற்கான சொத்து வரியை வீட்டு உரிமையாளர் செலுத்த வேண்டும்.",
            hi: "परिसर का संपत्ति कर मकान मालिक देगा।",
            mr: "मालमत्तेचा मालमत्ता कर घरमालक भरेल.",
            kn: "ಸ್ಥಳದ ಆಸ್ತಿ ತೆರಿಗೆಯನ್ನು ಮನೆ ಮಾಲಕರು ಪಾವತಿಸಬೇಕು.",
            te: "ఆస్తి పన్నును ఇంటి యజమాని చెల్లించాలి.",
            ml: "സ്വത്തിന്റെ നികുതി വീട്ടുടമ അടയ്ക്കേണ്ടതാണ്.",
            bn: "সম্পত্তির কর বাড়িওয়ালা পরিশোধ করবেন।",
            gu: "મિલકતનો વેરો મકાનમાલિક ચૂકવશે.",
        },
        "Property tax shall be paid by the tenant.": {
            ta: "சொத்து வரியை குத்தகைதாரர் செலுத்த வேண்டும்.",
            hi: "संपत्ति कर का भुगतान किरायेदार करेगा।",
            mr: "मालमत्ता कर भाडेकरू भरेल.",
            kn: "ಆಸ್ತಿ ತೆರಿಗೆಯನ್ನು ಬಾಡಿಗೆದಾರ ಪಾವತಿಸಬೇಕು.",
            te: "ఆస్తి పన్నును అద్దెదారు చెల్లించాలి.",
            ml: "സ്വത്ത് നികുതി വാടകക്കാരൻ അടയ്ക്കേണ്ടതാണ്.",
            bn: "সম্পত্তির কর ভাড়াটিয়া পরিশোধ করবেন।",
            gu: "મિલકત વેરો ભાડુઆત ચૂકવશે.",
        },
    },

    painting_clause: {
        "If any painting or repairs are required due to damages caused by the Tenant, the cost shall be deducted from the advance amount.": {
            ta: "குத்தகைதாரரால் ஏற்பட்ட சேதம் காரணமாக தேவைப்படும் வண்ணம் பூசுதல் அல்லது பழுது பார்ப்பதற்கான செலவு முன்பணத்திலிருந்து கழிக்கப்படும்.",
            hi: "किरायेदार द्वारा पहुंचाई गई किसी भी क्षति के कारण आवश्यक पेंटिंग या मरम्मत की लागत अग्रिम राशि से काटी जाएगी।",
            mr: "भाडेकरूने झालेल्या नुकसानीमुळे आवश्यक असलेल्या रंगरंगोटी किंवा दुरुस्तीचा खर्च अग्रिम रकमेतून वजा केला जाईल.",
            kn: "ಬಾಡಿಗೆದಾರರಿಂದ ಆದ ಹಾನಿಯಿಂದಾಗಿ ಅಗತ್ಯವಿರುವ ಬಣ್ಣ ಅಥವಾ ದುರಸ್ತಿಯ ಖರ್ಚನ್ನು ಮುಂಗಡ ಮೊತ್ತದಿಂದ ಕಡಿತಗೊಳಿಸಲಾಗುತ್ತದೆ.",
            te: "అద్దెదారు వల్ల జరిగిన నష్టం కారణంగా అవసరమైన పెయింటింగ్ లేదా మరమ్మతుల ఖర్చును ముందస్తు మొత్తం నుండి మినహాయించబడుతుంది.",
            ml: "വാടകക്കാരൻ വരുത്തിയ നാശം മൂലം ആവശ്യമായ പെയിന്റിംഗ് അല്ലെങ്കിൽ നന്നാക്കൽ ചെലവ് മുൻകൂർ തുകയിൽ നിന്ന് കുറയ്ക്കും.",
            bn: "ভাড়াটিয়া দ্বারা সৃষ্ট কোনো ক্ষতির কারণে যদি কোনো রঙ করা বা মেরামতের প্রয়োজন হয়, তার খরচ অগ্রিম টাকা থেকে কেটে নেওয়া হবে।",
            gu: "ભાડુઆત દ્વારા થયેલા નુકસાનના કારણે કોઈ રંગરોગાન અથવા મરામત જરૂરી હોય તો, તેનો ખર્ચ આગોતરી રકમમાંથી કાપવામાં આવશે.",
        },
        "Tenant shall paint the property at the time of vacating.": {
            ta: "வீட்டை காலி செய்யும் போது குத்தகைதாரர் வண்ணம் பூச வேண்டும்.",
            hi: "किरायेदार खाली करते समय संपत्ति को पेंट करेगा।",
            mr: "भाडेकरू मालमत्ता रिकामी करताना रंगवेल.",
            kn: "ಬಾಡಿಗೆದಾರ ಆಸ್ತಿಯನ್ನು ಖಾಲಿ ಮಾಡುವಾಗ ಬಣ್ಣ ಹಚ್ಚಬೇಕು.",
            te: "ఆస్తి ఖాళీ చేసే సమయంలో అద్దెదారు పెయింట్ చేయాలి.",
            ml: "സ്വത്ത് ഒഴിയുന്ന സമയത്ത് വാടകക്കാരൻ പെയിന്റ് ചെയ്യണം.",
            bn: "সম্পত্তি খালি করার সময় ভাড়াটিয়া রঙ করবেন।",
            gu: "મિલકત ખાલી કરતી વખતે ભાડુઆત રંગ કરશે.",
        },
    },

    maintenance_clause: {
        "Electrical and plumbing repairs are Landlord's responsibility. Minor repairs by Tenant.": {
            ta: "மின்சாரம் மற்றும் குழாய் பழுதுகள் வீட்டு உரிமையாளரின் பொறுப்பு. சிறிய பழுதுகள் குத்தகைதாரரால்.",
            hi: "बिजली और प्लंबिंग की मरम्मत मकान मालिक की जिम्मेदारी है। मामूली मरम्मत किरायेदार द्वारा।",
            mr: "विद्युत आणि नळ दुरुस्ती घरमालकाची जबाबदारी आहे. किरकोळ दुरुस्ती भाडेकरूने करावी.",
            kn: "ವಿದ್ಯುತ್ ಮತ್ತು ಕೊಳಾಯಿ ದುರಸ್ತಿಗಳು ಮನೆ ಮಾಲಕರ ಜವಾಬ್ದಾರಿ. ಸಣ್ಣ ದುರಸ್ತಿಗಳು ಬಾಡಿಗೆದಾರರಿಂದ.",
            te: "విద్యుత్ మరియు ప్లంబింగ్ మరమ్మతులు ఇంటి యజమాని బాధ్యత. చిన్న మరమ్మతులు అద్దెదారు చేయాలి.",
            ml: "വൈദ്യുതി, പ്ലംബിംഗ് നന്നാക്കലുകൾ വീട്ടുടമയുടെ ഉത്തരവാദിത്വം. ചെറിയ നന്നാക്കലുകൾ വാടകക്കാരൻ ചെയ്യണം.",
            bn: "বৈদ্যুতিক ও পাইপের মেরামত বাড়িওয়ালার দায়িত্ব। ছোটখাটো মেরামত ভাড়াটিয়া করবেন।",
            gu: "વીજળી અને પ્લમ્બિંગ મરામત મકાનમાલિકની જવાબદારી છે. નાની મરામત ભાડુઆત કરશે.",
        },
        "Tenant shall maintain the property in good condition.": {
            ta: "குத்தகைதாரர் சொத்தை நல்ல நிலையில் பராமரிக்க வேண்டும்.",
            hi: "किरायेदार संपत्ति को अच्छी स्थिति में बनाए रखेगा।",
            mr: "भाडेकरू मालमत्ता चांगल्या स्थितीत ठेवेल.",
            kn: "ಬಾಡಿಗೆದಾರ ಆಸ್ತಿಯನ್ನು ಉತ್ತಮ ಸ್ಥಿತಿಯಲ್ಲಿ ಕಾಪಾಡಬೇಕು.",
            te: "అద్దెదారు ఆస్తిని మంచి స్థితిలో నిర్వహించాలి.",
            ml: "വാടകക്കാരൻ സ്വത്തിനെ നല്ല അവസ്ഥയിൽ പരിപാലിക്കണം.",
            bn: "ভাড়াটিয়া সম্পত্তি ভালো অবস্থায় রক্ষণাবেক্ষণ করবেন।",
            gu: "ભાડુઆત મિલકતને સારી સ્થિતિમાં જાળવશે.",
        },
    },

    usage_clause: {
        "Residential purpose only. No subletting allowed.": {
            ta: "வீட்டு உபயோகத்திற்கு மட்டுமே. துணை-குத்தகை அனுமதிக்கப்படாது.",
            hi: "केवल आवासीय उपयोग के लिए। उप-किराया अनुमति नहीं है।",
            mr: "फक्त निवासी उद्देशासाठी. उप-भाडे परवानगी नाही.",
            kn: "ವಸತಿ ಉದ್ದೇಶಕ್ಕಾಗಿ ಮಾತ್ರ. ಉಪ-ಬಾಡಿಗೆ ಅನುಮತಿ ಇಲ್ಲ.",
            te: "నివాస అవసరాలకు మాత్రమే. ఉప-అద్దెకు అనుమతి లేదు.",
            ml: "വാസ ആവശ്യത്തിന് മാത്രം. ഉപവാടക അനുവദനീയമല്ല.",
            bn: "শুধুমাত্র আবাসিক উদ্দেশ্যে। উপ-ভাড়া দেওয়া অনুমোদিত নয়।",
            gu: "ફક્ત રહેણાંક હેતુ માટે. પેટા-ભાડા આપવાની અનુમતિ નથી.",
        },
        "The property shall be used only for residential purposes.": {
            ta: "சொத்தை வீட்டு உபயோகத்திற்கு மட்டுமே பயன்படுத்த வேண்டும்.",
            hi: "संपत्ति का उपयोग केवल आवासीय उद्देश्यों के लिए किया जाएगा।",
            mr: "मालमत्तेचा वापर फक्त निवासी उद्देशांसाठी केला जाईल.",
            kn: "ಆಸ್ತಿಯನ್ನು ವಸತಿ ಉದ್ದೇಶಗಳಿಗಾಗಿ ಮಾತ್ರ ಬಳಸಬೇಕು.",
            te: "ఆస్తిని నివాస అవసరాల కోసం మాత్రమే ఉపయోగించాలి.",
            ml: "സ്വത്ത് വാസ ആവശ്യങ്ങൾക്ക് മാത്രം ഉപയോഗിക്കണം.",
            bn: "সম্পত্তি শুধুমাত্র আবাসিক উদ্দেশ্যে ব্যবহার করা হবে।",
            gu: "મિલકતનો ઉપયોગ માત્ર રહેણાંક હેતુ માટે કરવામાં આવશે.",
        },
    },
};

/**
 * Resolve a stored clause value to the right string for a given language.
 *
 * @param {string} fieldKey    e.g. 'electricity_clause'
 * @param {string} storedValue the English text stored in terms[fieldKey]
 * @param {string} langCode    'en' | 'ta' | 'hi' | 'mr' | 'kn' | 'te' | 'ml' | 'bn' | 'gu'
 * @returns {string}  translation if found, else the original stored value
 */
export function resolveClause(fieldKey, storedValue, langCode) {
    if (!storedValue) return '';

    // New format: {en: "...", ta: "...", hi: "..."} — object with per-language
    // translations entered by the landlord in the edit UI. Pick the requested
    // language if present, else fall back to English, else empty string.
    if (typeof storedValue === 'object') {
        return storedValue[langCode] || storedValue.en || '';
    }

    // Legacy format: plain string. English returns as-is; other languages
    // try the CLAUSE_LIBRARY lookup by exact-English-match.
    if (langCode === 'en') return storedValue;
    const field = CLAUSE_LIBRARY[fieldKey];
    if (!field) return storedValue;
    const translations = field[storedValue];
    if (!translations) return storedValue;
    return translations[langCode] || storedValue;
}

/**
 * Check whether a stored value is a known library entry (used by the UI
 * to decide whether to show "standard clause, will auto-translate" vs
 * "custom text, stays as you wrote it").
 */
export function isLibraryClause(fieldKey, storedValue) {
    return !!CLAUSE_LIBRARY[fieldKey]?.[storedValue];
}

/**
 * List the library options for a given clause field. Useful for building
 * a dropdown in the Agreement-edit UI. Returns { id, preview } where
 * `id` is the English sentence (which is what gets stored) and `preview`
 * is shown in the dropdown.
 */
export function getClauseLibraryOptions(fieldKey, previewLang = 'en') {
    const field = CLAUSE_LIBRARY[fieldKey];
    if (!field) return [];
    return Object.keys(field).map((englishText) => ({
        id: englishText,
        preview: previewLang === 'en'
            ? englishText
            : (field[englishText][previewLang] || englishText),
    }));
}
